import React, { useEffect, useState } from 'react';
import LoginTextField from '../elements/editors/mui/LoginTextField';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import './SetPasswordForm.scss';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { CHECK_PASSWORD_REQUEST, checkPasswordStrength } from 'new/stores/changePassword/action';
import styles from 'new/components/settings/SecurityNPrivacy/SecurityNPrivacy.module.scss';
import { CircularProgress } from '@mui/material';
import { useDebouncedCallback } from 'new/hooks/useDebouncedCallback';
import { StringUtil } from 'new/utils/StringUtil';
import { Button } from 'new/elements/Button';

const { capitalizeArrayOfString } = StringUtil;

function SetPasswordForm(props) {
  const {
    register,
    formState: { errors, isValid, dirtyFields },
    watch,
    getValues,
    handleSubmit,
  } = useForm({
    mode: 'onChange',
    criteriaMode: 'all',
  });

  const [checked, setChecked] = useState(false);
  const dispatch = useDispatch();

  const entropyErrors = useSelector((state) => state.changePassword?.entropy?.errors);
  const checkingPassword = useIsLoading(CHECK_PASSWORD_REQUEST);

  const password = watch().password;

  const minLengthRule = dirtyFields.password && !errors?.password?.types['minLength'];
  const hasAlphabeticalLowerRule =
    dirtyFields.password && !errors?.password?.types['hasAlphabeticalLower'];
  const hasAlphabeticalUpperRule =
    dirtyFields.password && !errors?.password?.types['hasAlphabeticalUpper'];
  const hasNumberRule = dirtyFields.password && !errors?.password?.types['hasNumber'];
  const hasSpecialCharRule = dirtyFields.password && !errors?.password?.types['hasSpecialChar'];
  const isStrongEnough = !!entropyErrors && !entropyErrors.length;

  const onKeyUp = (e) => {
    if (['Enter', 'enter'].includes(e.key)) {
      dispatch(checkPasswordStrength(password)).then(() => {
        setChecked(true);
      });
    }
  };

  const checkPasswordEffect = useDebouncedCallback(
    () => {
      if (
        minLengthRule &&
        hasAlphabeticalUpperRule &&
        hasAlphabeticalLowerRule &&
        hasNumberRule &&
        hasSpecialCharRule &&
        !checked &&
        !checkingPassword
      ) {
        dispatch(checkPasswordStrength(password)).then(() => {
          setChecked(true);
        });
      }
    },
    [
      password,
      checkingPassword,
      checked,
      minLengthRule,
      hasAlphabeticalUpperRule,
      hasAlphabeticalLowerRule,
      hasNumberRule,
      hasSpecialCharRule,
    ],
    500,
  );

  useEffect(checkPasswordEffect, [checkPasswordEffect]);

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      <h2>Enter a new password</h2>
      <form onSubmit={handleSubmit(props.onSubmit)}>
        <div className="signup-form-item">
          <LoginTextField
            autoFocus
            type="password"
            label="New Password"
            autoComplete="new-password"
            onKeyUp={onKeyUp}
            error={!!errors.password || (checked && entropyErrors?.length > 0)}
            helperText={
              errors.password || (checked && entropyErrors?.length > 0) ? (
                <>
                  {errors.password?.message || capitalizeArrayOfString(entropyErrors).join(' - ')}
                </>
              ) : undefined
            }
            {...register('password', {
              onChange: () => setChecked(false),
              required: 'Please, input the password',
              validate: {
                minLength: (value = '') => {
                  return value.length >= 8 || 'Password needs to have at least 8 characters';
                },
                hasAlphabeticalLower: (value) => {
                  return (
                    /[a-z]/.test(value) ||
                    'Needs to have at least one lower case alphabetical character'
                  );
                },
                hasAlphabeticalUpper: (value) => {
                  return (
                    /[A-Z]/.test(value) ||
                    'Needs to have at least one upper case alphabetical character'
                  );
                },
                hasNumber: (value) => {
                  return /\d/.test(value) || 'Needs to have at least one number';
                },
                hasSpecialChar: (value) => {
                  return (
                    /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value) ||
                    'Needs to have at least one special character'
                  );
                },
              },
            })}
          />
        </div>
        <div className="signup-form-item">
          <LoginTextField
            required
            type="password"
            label="Confirm New Password"
            error={!!errors.password_confirmation}
            helperText={
              errors.password_confirmation ? (
                <>{errors.password_confirmation?.message}</>
              ) : undefined
            }
            {...register('password_confirmation', {
              required: 'Please, input the password confirmation',
              validate: {
                equalsConfirmation: (value) =>
                  value === getValues().password ||
                  'Confirmation password needs to be equal to password',
              },
            })}
          />
        </div>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          style={{ width: '100%' }}
          disabled={!isValid || !isStrongEnough || !checked}
        >
          Set new Password
        </Button>
      </form>
      <div className="password-rules-holder">
        <h4>Password Requirements</h4>
        <div>
          <ol className={styles.passwordOLList}>
            <li className={minLengthRule ? 'greenCheck' : ''}>
              Be a minimum of 8 characters in length.
            </li>
            <li
              className={
                hasSpecialCharRule &&
                hasNumberRule &&
                hasAlphabeticalLowerRule &&
                hasAlphabeticalUpperRule
                  ? 'greenCheck'
                  : ''
              }
            >
              Include at least one of each:
              <ul>
                <li className={hasAlphabeticalUpperRule ? 'greenCheck' : ''}>
                  Upper case alphabetical letter (A-Z)
                </li>
                <li className={hasAlphabeticalLowerRule ? 'greenCheck' : ''}>
                  Lower case alphabetical letter (a-z)
                </li>
                <li className={hasNumberRule ? 'greenCheck' : ''}>Numeral (0-9)</li>
                <li className={hasSpecialCharRule ? 'greenCheck' : ''}>
                  Non-alphanumeric character (symbol or punctuation mark)
                </li>
              </ul>
            </li>
            <li className={isStrongEnough && checked ? 'greenCheck' : ''}>
              {checkingPassword ? (
                <CircularProgress size={12} className={'passwordCheckLoader'} />
              ) : null}
              Password should be strong enough to not be easily guessable
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}

export default SetPasswordForm;
